import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type AgentIdentity, type SafeUser } from "../lib/api";
import { useToast } from "./toast/ToastProvider";

interface IdentityCardProps {
  user: SafeUser;
}

// IdentityCard is the first card on the Settings page. Plant ID is
// effectively the agent's primary identity — the central catalog uses it
// to scope every URN, and standalone artifacts ship with it stamped on the
// manifest. Setting it is required before the runner will build artifacts.
export function IdentityCard({ user }: IdentityCardProps) {
  const qc = useQueryClient();
  const { show } = useToast();
  const identity = useQuery({
    queryKey: ["identity"],
    queryFn: () => api.get<AgentIdentity>("/api/v1/agent/identity"),
  });

  const [plantId, setPlantId] = useState("");
  const [plantName, setPlantName] = useState("");
  const [agentLabel, setAgentLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Hydrate the form once data arrives.
  useEffect(() => {
    if (!identity.data) return;
    setPlantId(identity.data.plantId);
    setPlantName(identity.data.plantName);
    setAgentLabel(identity.data.agentLabel);
  }, [identity.data]);

  const save = useMutation({
    mutationFn: (body: Partial<AgentIdentity>) =>
      api.patch<AgentIdentity>("/api/v1/agent/identity", body),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      show("Identity saved","Agent identity updated","success");
      setTimeout(() => setSaved(false), 2000);
      qc.invalidateQueries({ queryKey: ["identity"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (e) => { const m=e instanceof Error ? e.message : "save failed"; setError(m); show("Save failed",m,"error"); },
  });

  const isAdmin = user.role === "admin";
  const dirty =
    identity.data &&
    (identity.data.plantId !== plantId ||
      identity.data.plantName !== plantName ||
      identity.data.agentLabel !== agentLabel);
  const willChangePlantID =
    identity.data && identity.data.plantId && identity.data.plantId !== plantId;

  return (
    <section className="card overflow-hidden">
      <div className="border-b border-rule px-6 py-5">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink">
            Agent identity
          </h2>
          {identity.data?.enrolled ? (
            <span className="chip-emerald">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Enrolled
            </span>
          ) : (
            <span className="chip-neutral">Standalone</span>
          )}
        </div>
        <p className="mt-2 max-w-xl text-[13.5px] leading-relaxed text-ash">
          Plant ID is stamped onto every artifact this agent produces. The
          central catalog uses it to namespace URNs so two plants with the same
          upstream Kepware project don't collide. <strong>Required</strong>{" "}
          before any crawl can build an artifact.
        </p>
      </div>

      <div className="grid gap-5 px-6 py-5 sm:grid-cols-2">
        <Field
          label="Plant ID"
          hint={
            <>
              Lowercase letters, digits, <code className="font-mono">.</code>{" "}
              <code className="font-mono">_</code>{" "}
              <code className="font-mono">-</code>. Cannot contain spaces.
            </>
          }
        >
          <input
            value={plantId}
            onChange={(e) => setPlantId(e.target.value.toLowerCase().trim())}
            placeholder="plant-001"
            disabled={!isAdmin}
            className="input font-mono"
            pattern="[a-z0-9._-]{1,64}"
          />
        </Field>
        <Field label="Plant display name">
          <input
            value={plantName}
            onChange={(e) => setPlantName(e.target.value)}
            placeholder="Toledo Refinery"
            disabled={!isAdmin}
            className="input"
          />
        </Field>
        <Field
          label="Agent label"
          className="sm:col-span-2"
          hint="Optional. Helps disambiguate when one plant has multiple agent hosts."
        >
          <input
            value={agentLabel}
            onChange={(e) => setAgentLabel(e.target.value)}
            placeholder="PLC-room-east"
            disabled={!isAdmin}
            className="input"
          />
        </Field>
        {identity.data?.agentId ? (
          <Field
            label="Agent ID"
            className="sm:col-span-2"
          >
            <input
              value={identity.data.agentId}
              readOnly
              className="input font-mono text-ash cursor-default select-all"
            />
          </Field>
        ) : null}
      </div>

      {willChangePlantID ? (
        <div className="mx-6 mb-5 rounded-md border border-amber-500/30 bg-amber-50 px-3 py-2 text-[12.5px] leading-relaxed text-amber-700">
          <strong>Heads up.</strong> Changing the plant ID after artifacts have
          shipped under the old one will make the central catalog see two
          distinct plants. Only do this if no artifacts have been delivered yet.
        </div>
      ) : null}

      {error ? (
        <div className="mx-6 mb-5 rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
          {error}
        </div>
      ) : null}

      {isAdmin ? (
        <div className="flex items-center justify-end gap-3 border-t border-rule px-6 py-4">
          {saved ? (
            <span className="text-[12px] text-emerald-700">Saved.</span>
          ) : null}
          <button
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate({
                plantId,
                plantName,
                agentLabel,
              })
            }
            className="btn-primary"
          >
            {save.isPending ? "Saving…" : "Save identity"}
          </button>
        </div>
      ) : (
        <div className="border-t border-rule px-6 py-3 text-[12px] text-ash">
          Read-only. Sign in as an administrator to edit.
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  children,
  hint,
  className,
}: {
  label: string;
  children: React.ReactNode;
  hint?: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={"block " + (className ?? "")}>
      <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
        {label}
      </span>
      {children}
      {hint ? (
        <span className="mt-1.5 block text-[11.5px] text-ash">{hint}</span>
      ) : null}
    </label>
  );
}
